/* eslint-disable react/no-danger */

import React, { useEffect, useContext, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import {
  PrivacySettingToggle,
  PrivacySettingToggles,
} from '@prisjakt/web-app-config/types/market';
import T from '@prisjakt/ui-translate';
import { AppContext } from '@prisjakt/ui-app-context';
import { NudgeDown } from '@prisjakt/ui/hideable/variants';
import { Speed } from '@prisjakt/ui/definitions';
import { ViewportContext } from '../viewport';
import CtxLink from '../render-context/ctx-link';
import { pushEvent } from '../../client/data-layer';
import acceptCookiesAction from './actions';
import { AppState } from '../../reducers';

import * as S from './styles';

type OwnProps = {};

type StateProps = {
  version: string;
  settings: PrivacySettingToggle;
  isLoggedIn: boolean;
  isActive: boolean;
};

type DispatchProps = {
  acceptCookies: typeof acceptCookiesAction;
};

type Props = OwnProps & StateProps & DispatchProps;

const containerProps = {
  id: 'cookie-banner',
  'data-test': 'CookieBanner',
};

/**
  Cookie banner reads state from localstorage and/or cookie.
  Because of this we cannot render the cookie banner server side
  as usual since users with previous accepts will get a "flash-
  of-already-accepted-cookie-banner".
* */
const CookieBanner = ({
  version,
  acceptCookies,
  settings,
  isLoggedIn,
  isActive,
}: Props) => {
  const { t, renderMode, market } = useContext(AppContext);
  const cdn = market && market.cdn ? market.cdn : 'https://cdn.pji.nu';
  const { isDesktop } = useContext(ViewportContext);
  const privacySettings =
    market &&
    market.privacySettings &&
    (market.privacySettings[renderMode] || market.privacySettings.responsive);

  useEffect(() => {
    const visible = !window.SESSION_DATA.session.cookie_consent_version;
    pushEvent('cookieBannerIsVisible', { visible });
  }, []);

  const onAcceptCookies = useCallback(() => {
    acceptCookies(
      privacySettings.cookieVersion,
      {
        ...settings,
        [PrivacySettingToggles.BehaviorTrackingMarketing]: true,
      },
      isLoggedIn
    );
  }, [isLoggedIn, privacySettings, acceptCookies, settings]);

  const cookiePolicyLink = useMemo(
    () => (
      <CtxLink to={privacySettings.cookieUrl} key="cookie">
        {t('common', 'cookie-banner.cookie-policy')}
      </CtxLink>
    ),
    [t, privacySettings.cookieUrl]
  );

  const privacyPolicyLink = useMemo(
    () => (
      <CtxLink to={privacySettings.privacyUrl} key="privacy">
        {t('common', 'cookie-banner.privacy-policy')}
      </CtxLink>
    ),
    [t, privacySettings.privacyUrl]
  );

  if (!isActive) {
    return null;
  }

  if (renderMode === 'native') {
    return null;
  }

  const rootElement = document.getElementById('root-cookiebanner');

  if (!rootElement) {
    return null;
  }

  const logo = (
    <S.LogoWrapper>
      <S.Logo>
        <img
          alt="Schibsted"
          src={`${cdn}/g/rfe/logos/schibsted/schibsted_next_white.png`}
          width="116"
        />
      </S.Logo>
    </S.LogoWrapper>
  );

  const isVisible = version !== privacySettings.cookieVersion;

  const banner = (
    <NudgeDown
      visible={isVisible}
      Container={S.Banner}
      containerProps={containerProps}
      speed={Speed.Slow}
    >
      <S.BannerContent>
        <S.BannerText>
          {!isDesktop && logo}
          <T
            domain="common"
            cookiePolicyLink={cookiePolicyLink}
            privacyPolicyLink={privacyPolicyLink}
          >
            cookie-banner.description
          </T>
        </S.BannerText>

        <S.ButtonsWrapper>
          <S.AcceptButton color="primary" onClick={onAcceptCookies}>
            <T domain="common">cookie-banner.accept-button</T>
          </S.AcceptButton>
          <S.Button
            href={privacySettings.privacySettingsUrl}
            color="tertiary"
            variant="outlined"
          >
            {t('common', 'cookie-banner.privacy-policy-settings')}
          </S.Button>
        </S.ButtonsWrapper>

        {isDesktop && logo}
      </S.BannerContent>
    </NudgeDown>
  );

  return createPortal(banner, rootElement);
};

const mapStateToProps = (state: AppState) => ({
  version: state.globals.cookieBanner.version,
  isActive: state.globals.cookieBanner.isActive,
  settings: state.areas.user.tracking.settings,
  isLoggedIn: state.areas.account.isAuthenticated,
});

const dispatchProps = {
  acceptCookies: acceptCookiesAction,
};

export default connect(mapStateToProps, dispatchProps)(CookieBanner);
